import { useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  HelpCircle, X, Search, MessageSquare, Brain, Heart, Volume2,
  Bookmark, Shield, HardDrive, Share2, Target, Users, Sparkles,
  BookOpen, Zap, Palette, Globe, Compass, Layers, ChevronRight,
  Keyboard, Gift, Calendar, CreditCard, Settings, ArrowRight, Trash2, Mic
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger
} from "@/components/ui/sheet";

const HELP_CATEGORIES = [
  {
    id: "getting-started",
    label: "Getting Started",
    color: "text-emerald-600 dark:text-emerald-400",
    bg: "bg-emerald-500/10",
    items: [
      { icon: MessageSquare, title: "Chat with Omni", desc: "Start a conversation with any of 7 AI guides", route: "/chat", guideId: "modules" },
      { icon: Zap, title: "Quick Templates", desc: "18 curated prompts to jump into meaningful chats", route: "/chat", guideId: "templates" },
      { icon: Layers, title: "Response Mode", desc: "Toggle between Concise and Comprehensive replies", route: "/chat", guideId: "response-mode" },
    ],
  },
  {
    id: "ai-modes",
    label: "AI Modes & Guides",
    color: "text-primary",
    bg: "bg-primary/10",
    items: [
      { icon: MessageSquare, title: "6 Chat Modules", desc: "Friend, Mentor, Social Guide, Job Guardian, Life Coach, Storyteller", route: "/guide#modules", guideId: "modules" },
      { icon: Sparkles, title: "Wisdom Council", desc: "22 historical mentors with unique AI voices", route: "/guide#wisdom", guideId: "wisdom" },
      { icon: Globe, title: "Cultural Awareness", desc: "Standard or Ultra Cultural mode for heritage-aware AI", route: "/settings", guideId: "languages" },
    ],
  },
  {
    id: "features",
    label: "Core Features",
    color: "text-blue-600 dark:text-blue-400",
    bg: "bg-blue-500/10",
    items: [
      { icon: Brain, title: "AI Memory", desc: "Omni learns and remembers facts about you over time", route: "/chat", guideId: "memory" },
      { icon: Mic, title: "Microphone Input", desc: "Tap the mic to speak instead of type — or say \"Hey Omni\" to go hands-free", route: "/chat", guideId: "voice" },
      { icon: Volume2, title: "Voice & TTS", desc: "Speak to Omni and hear responses read aloud", route: "/chat", guideId: "voice" },
      { icon: Heart, title: "Mood Tracking", desc: "Log emotions, view trends, and get mood-aware responses", route: "/mood", guideId: "mood" },
      { icon: Calendar, title: "Calendar & Timeline", desc: "Activity history, streaks, and session timeline", route: "/calendar", guideId: null },
      { icon: Target, title: "Goals", desc: "Set and track personal goals with AI coaching", route: "/goals", guideId: null },
      { icon: Bookmark, title: "Bookmarks", desc: "Save any message for quick access later", route: "/bookmarks", guideId: "bookmarks" },
    ],
  },
  {
    id: "sharing",
    label: "Sharing & Social",
    color: "text-violet-600 dark:text-violet-400",
    bg: "bg-violet-500/10",
    items: [
      { icon: Share2, title: "Social Sharing", desc: "Share insights on Twitter, WhatsApp, LinkedIn & more", route: "/share-portal", guideId: "sharing" },
      { icon: Gift, title: "Gift Subscription", desc: "Gift Omni Premium to friends and family", route: "/gift", guideId: null },
      { icon: Users, title: "Family Plan", desc: "Multi-member support with age-appropriate controls", route: "/family", guideId: null },
    ],
  },
  {
    id: "settings-data",
    label: "Settings & Data",
    color: "text-amber-600 dark:text-amber-400",
    bg: "bg-amber-500/10",
    items: [
      { icon: Palette, title: "AI Personality", desc: "Customize style, humor, sensitivity, and formality", route: "/settings", guideId: "settings" },
      { icon: Globe, title: "113 Languages", desc: "6 free + 107 premium languages with TTS support", route: "/settings", guideId: "languages" },
      { icon: HardDrive, title: "Backup & Export", desc: "Auto-backup conversations, memories, and mood logs", route: "/settings", guideId: "backup" },
      { icon: Trash2, title: "Delete My History", desc: "Wipe conversations, mood logs & memories anytime — keep your account", route: "/settings", guideId: null },
      { icon: Shield, title: "Subscription Tiers", desc: "Compare plans and unlock premium features", route: "/pricing", guideId: "tiers" },
    ],
  },
];

const SHORTCUTS = [
  { keys: ["Ctrl", "N"], label: "New chat" },
  { keys: ["Ctrl", "K"], label: "Search" },
  { keys: ["Ctrl", "M"], label: "Module menu" },
  { keys: ["Ctrl", "D"], label: "Dark mode" },
  { keys: ["Ctrl", "B"], label: "Bulk mode" },
];

export function HelpCenter() {
  const navigate = useNavigate();
  const location = useLocation();
  const isChatPage = location.pathname.startsWith("/chat");
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("all");

  const allItems = useMemo(() =>
    HELP_CATEGORIES.flatMap(cat =>
      cat.items.map(item => ({ ...item, categoryId: cat.id, categoryLabel: cat.label }))
    ), []
  );

  const filtered = useMemo(() => {
    if (!search.trim()) return activeTab === "all" ? HELP_CATEGORIES : HELP_CATEGORIES.filter(c => c.id === activeTab);
    const q = search.toLowerCase();
    return HELP_CATEGORIES.map(cat => ({
      ...cat,
      items: cat.items.filter(item =>
        item.title.toLowerCase().includes(q) ||
        item.desc.toLowerCase().includes(q)
      ),
    })).filter(cat => cat.items.length > 0);
  }, [search, activeTab]);

  const handleItemClick = (item) => {
    setOpen(false);
    if (item.guideId) {
      navigate(`/guide#${item.guideId}`);
    } else if (item.route) {
      navigate(item.route);
    }
  };

  const tabs = [
    { id: "all", label: "All" },
    { id: "getting-started", label: "Start" },
    { id: "ai-modes", label: "Modes" },
    { id: "features", label: "Features" },
    { id: "sharing", label: "Social" },
    { id: "settings-data", label: "Settings" },
  ];

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          data-testid="help-center-btn"
          className={cn(
            "fixed z-40 right-4 md:right-6",
            isChatPage ? "bottom-40 md:bottom-28" : "bottom-24 md:bottom-6",
            "w-12 h-12 rounded-full",
            "bg-primary text-primary-foreground shadow-lg shadow-primary/25",
            "flex items-center justify-center",
            "hover:scale-110 active:scale-95 transition-all duration-200",
            "ring-2 ring-primary/20 ring-offset-2 ring-offset-background"
          )}
          aria-label="Help Center"
        >
          <HelpCircle className="w-5 h-5" strokeWidth={2} />
        </button>
      </SheetTrigger>

      <SheetContent
        side="right"
        className="w-full sm:w-[420px] p-0 flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="px-5 pt-5 pb-3 border-b border-border/50 space-y-3 flex-shrink-0">
          <SheetHeader className="p-0">
            <SheetTitle className="flex items-center gap-2.5 text-lg font-heading font-bold">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <BookOpen className="w-4 h-4 text-primary" strokeWidth={2} />
              </div>
              Help Center
            </SheetTitle>
          </SheetHeader>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              data-testid="help-search-input"
              type="text"
              placeholder="Search features, modes, guides..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-border/60 bg-muted/30 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all placeholder:text-muted-foreground/50"
            />
          </div>

          {/* Tabs */}
          <div className="flex gap-1 overflow-x-auto pb-0.5 scrollbar-hide">
            {tabs.map(tab => (
              <button
                key={tab.id}
                data-testid={`help-tab-${tab.id}`}
                onClick={() => { setActiveTab(tab.id); setSearch(""); }}
                className={cn(
                  "px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all",
                  activeTab === tab.id
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
          {filtered.map(cat => (
            <div key={cat.id}>
              <p className={cn("text-[11px] font-bold uppercase tracking-wider mb-2 px-1", cat.color)}>
                {cat.label}
              </p>
              <div className="space-y-1">
                {cat.items.map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={i}
                      data-testid={`help-item-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
                      onClick={() => handleItemClick(item)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-muted/60 transition-all text-left group"
                    >
                      <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0", cat.bg)}>
                        <Icon className={cn("w-4 h-4", cat.color)} strokeWidth={1.8} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium leading-tight">{item.title}</p>
                        <p className="text-xs text-muted-foreground leading-snug mt-0.5 line-clamp-1">{item.desc}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground/40 group-hover:text-muted-foreground transition-colors flex-shrink-0" />
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {filtered.length === 0 && search && (
            <div className="text-center py-10">
              <Search className="w-8 h-8 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No results for "{search}"</p>
              <button
                onClick={() => setSearch("")}
                className="text-xs text-primary mt-2 hover:underline"
              >
                Clear search
              </button>
            </div>
          )}

          {/* Keyboard Shortcuts */}
          {activeTab === "all" && !search && (
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider mb-2 px-1 text-muted-foreground/60">
                Keyboard Shortcuts
              </p>
              <div className="bg-muted/30 rounded-xl p-3 space-y-2">
                {SHORTCUTS.map((s, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{s.label}</span>
                    <div className="flex gap-1">
                      {s.keys.map((k, j) => (
                        <kbd key={j} className="px-1.5 py-0.5 rounded bg-background border border-border/60 text-[10px] font-mono text-muted-foreground">
                          {k}
                        </kbd>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Full Guide CTA */}
          <button
            data-testid="help-full-guide-btn"
            onClick={() => { setOpen(false); navigate("/guide"); }}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-primary/20 bg-primary/[0.03] hover:bg-primary/[0.06] text-sm font-medium text-primary transition-all"
          >
            <BookOpen className="w-4 h-4" />
            Open Full Feature Guide
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
